import { Skeleton, Banner, Flex } from '@sequencehq/core-components'
import { ConfigurationManagementProps } from 'Integrations/domain'
import { useForm } from '@sequencehq/utils'
import {
  QuickBooksConfig,
  QuickBooksTaxCodeMapping
} from 'Integrations/integrationsConfig/quickBooks/quickbooks.integration.config'
import { useMemo } from 'react'
import deepmerge from 'deepmerge'
import { DefaultQuickBooksAccountCard } from 'Integrations/integrationsConfig/quickBooks/QuickBooksConfigurationManagement/DefaultQuickBooksAccountCard'
import { TaxCodesCard } from 'Integrations/integrationsConfig/quickBooks/QuickBooksConfigurationManagement/TaxCodesCard'
import invariant from 'tiny-invariant'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { MINUTES } from '@sequencehq/utils/dates'
import { AutoLinkCustomersCard } from 'Integrations/integrationsConfig/common/AutoLinkCustomers/AutoLinkCustomersCard'
import { IntegrationActiveToggle } from 'Integrations/integrationsConfig/common/IntegrationActiveToggle'
import { PaymentBankAccountsCard } from 'Integrations/integrationsConfig/common/PaymentBankAccounts/PaymentBankAccountsCard'
export const QuickBooksConfigurationManagement = (
  props: ConfigurationManagementProps
) => {
  invariant(props.integration.id === 'QuickBooks_Online', 'Invalid integration')

  const configuration = props.integration.configuration as QuickBooksConfig

  const mappings = configuration?.taxCodeMappings as Record<
    string,
    QuickBooksTaxCodeMapping
  >

  const taxCodeMappingFields: { property: `taxCodeMappings.${string}` }[] =
    Object.keys(mappings).map(taxCategoryId => ({
      property: `taxCodeMappings.${taxCategoryId}`
    }))

  // autoLinkSyncPermission is a read-only, backend-resolved value that drives
  // the paused badge — it is not an editable form field, so keep it out of
  // useForm (otherwise it becomes a phantom field that breaks when absent).
  const { autoLinkSyncPermission, ...formValue } = (configuration ?? {
    importCustomerWorkflow: false,
    taxExemptTaxCode: '',
    taxableTaxCode: '',
    reverseChargedTaxCode: '',
    defaultAccount: '',
    taxCodeMappings: {},
    autoLinkCustomers: false,
    paymentBankAccounts: {}
  }) as QuickBooksConfig

  const { fields } = useForm({
    value: formValue,
    fieldConfiguration: [
      {
        property: 'importCustomerWorkflow'
      },
      {
        property: 'autoLinkCustomers'
      },
      {
        property: 'taxExemptTaxCode'
      },
      {
        property: 'taxableTaxCode'
      },
      {
        property: 'reverseChargedTaxCode'
      },
      {
        property: 'defaultAccount'
      },
      {
        property: 'taxCodeMappings'
      },
      {
        property: 'paymentBankAccounts'
      },
      ...taxCodeMappingFields
    ],
    onChange: newData => {
      props.updateConfiguration(newData)
    },
    onValidationStateChange: isValid => {
      props.updateValidationState(isValid)
    }
  })

  const fieldsConfig = useMemo(() => {
    return deepmerge(fields, {
      importCustomerWorkflow: {
        status: props.integration.configuration?.importCustomerWorkflow
          ? ('ACTIVE' as const)
          : ('INACTIVE' as const)
      }
    })
  }, [fields, props.integration])

  // useForm also exposes a field per nested key, and TaxCodesCard reads only the
  // `taxCodeMappings.<id>` ones from the rest — so drop the payment keys here.
  const {
    taxExemptTaxCode,
    taxableTaxCode,
    reverseChargedTaxCode,
    importCustomerWorkflow,
    defaultAccount,
    autoLinkCustomers,
    paymentBankAccounts,
    ...dynamicTaxCodeFields
  }: Omit<typeof fieldsConfig, `paymentBankAccounts.${string}`> = fieldsConfig

  const autoLinkPaused = Boolean(
    autoLinkCustomers.value &&
      autoLinkSyncPermission &&
      autoLinkSyncPermission !== 'ALLOW'
  )

  const defaultTaxCodeFields = {
    taxExemptTaxCode,
    taxableTaxCode,
    reverseChargedTaxCode
  }

  const { data: qboAccountsData, isLoading: isLoadingQboAccounts } = useQuery(
    dashboardv99990101Client.getQuickbooksAccounts,
    undefined,
    {
      enabled: props.integration.hasWorkingConnection
    }
  )
  const { data: quickbooksTaxCodesData, isLoading: isLoadingQboTaxCodes } =
    useQuery(dashboardv99990101Client.getQuickbooksTaxCodes, undefined, {
      enabled: props.integration.hasWorkingConnection
    })
  // Read from the same cache entry the configuration loader populated.
  const { data: fullIntegration, isLoading: isLoadingIntegration } = useQuery(
    dashboard20240730Client.getIntegration,
    'QuickBooks_Online',
    {
      staleTime: 1 * MINUTES,
      enabled: props.integration.hasWorkingConnection
    }
  )

  const bankAccounts =
    fullIntegration?.service === 'QuickBooks_Online'
      ? fullIntegration.bankAccounts
      : []

  const quickbooksAccounts = qboAccountsData?.items ?? []

  const quickbooksTaxCodes = quickbooksTaxCodesData?.items ?? []

  // Check if default account and tax codes are set
  const isDefaultAccountMissing = !defaultAccount.value
  const hasAllTaxCodeDefaults = [
    taxExemptTaxCode,
    taxableTaxCode,
    reverseChargedTaxCode
  ].every(({ value }) => value?.length)

  // Show warning banner if any required settings are missing
  const showWarningBanner = isDefaultAccountMissing || !hasAllTaxCodeDefaults

  const getWarningMessage = () => {
    const hasOnlyDefaultAccountMissing =
      isDefaultAccountMissing && hasAllTaxCodeDefaults

    const hasOnlyTaxCodesMissing =
      !isDefaultAccountMissing && !hasAllTaxCodeDefaults

    if (hasOnlyDefaultAccountMissing) {
      return 'Set a default account code for accurate invoice syncing.'
    }

    if (hasOnlyTaxCodesMissing) {
      return 'Map customer tax codes (exempt, reverse charged and fallback) for accurate invoice syncing.'
    }

    return 'Set a default account code and map customer tax codes (exempt, reverse charged and fallback) for accurate invoice syncing.'
  }

  if (isLoadingQboAccounts || isLoadingQboTaxCodes || isLoadingIntegration) {
    return (
      <Flex flexDirection="column" gap="20px" width="100%">
        <Skeleton width="100%" height="165px" />
        <Skeleton width="100%" height="174px" />
        <Skeleton width="100%" height="476px" />
      </Flex>
    )
  }

  return (
    <Flex
      flexDirection="column"
      gap="20px"
      width="100%"
      data-testid="quickbooksIntegration.configurationForm"
    >
      {showWarningBanner && (
        <Banner
          variant="warning"
          alignIcon="center"
          data-testid="quickbooks.configuration.warning"
        >
          {getWarningMessage()}
        </Banner>
      )}
      <IntegrationActiveToggle
        checked={fieldsConfig.importCustomerWorkflow.value}
        active={fieldsConfig.importCustomerWorkflow.status === 'ACTIVE'}
        onChange={fieldsConfig.importCustomerWorkflow.onChange}
      />
      <AutoLinkCustomersCard
        service="QuickBooks_Online"
        value={autoLinkCustomers.value}
        paused={autoLinkPaused}
        onChange={autoLinkCustomers.onChange}
      />
      <DefaultQuickBooksAccountCard
        quickbooksAccounts={quickbooksAccounts}
        fieldsConfig={fieldsConfig}
      />
      <TaxCodesCard
        taxCodeDefaultsFieldsConfig={defaultTaxCodeFields}
        taxCodeMappingFieldsConfig={dynamicTaxCodeFields}
        mappings={mappings}
        quickbooksTaxCodes={quickbooksTaxCodes}
      />
      <PaymentBankAccountsCard
        data-testid="quickbooksIntegration.paymentBankAccounts"
        serviceName="QuickBooks"
        bankAccounts={bankAccounts}
        value={paymentBankAccounts.value}
        onChange={paymentBankAccounts.onChange}
      />
    </Flex>
  )
}
